import "@/components/history/history.css";
import { historyWords as words, historyText as text } from "@/components/history/history-copy";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { Bento, PageRefusal } from "@/components/ui/box";
import { courseHistoryApi, type CourseHistoryFilters, type CourseHistoryPage as HistoryPage } from "@/lib/api/courseHistory";
import { ApiError } from "@/lib/api/client";
import { historyBox, historyRead } from "@/components/history/history-boxes";
import { historyCopy as copy } from "@/components/history/history-copy";
import { searchWithTrail } from "@/lib/visit/address";
import type { ListFacet } from "@/components/ui/box.schema";
import { RegistrarLocalNavigation } from "@/components/registrar/RegistrarLocalNavigation";

export function CourseHistoryPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const legacyId = params.get("course-history");
  useEffect(() => {
    if (!legacyId) return;
    const next = new URLSearchParams(params);
    next.delete("course-history");
    navigate({ search: searchWithTrail(next.toString(), [`course:${legacyId}/history`]) }, { replace: true });
  }, [legacyId, params, navigate]);
  const filters: CourseHistoryFilters = Object.fromEntries([...params].filter(([key]) =>
    ["q", "term_id", "program_id", "faculty_id", "provenance", "roster", "credits", "outcomes", "attendance", "materials"].includes(key)));
  const continuation = params.get("has_continuation");
  if (continuation === "true" || continuation === "false") filters.has_continuation = continuation === "true";
  filters.page = Math.max(1, Number(params.get("page")) || 1);
  filters.page_size = 25;
  const query = useQuery({ queryKey: ["course-history", filters], queryFn: () => courseHistoryApi.list(filters) });
  const filter = (key: string, value: string) => setParams((before) => {
    const next = new URLSearchParams(before);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== "page") next.delete("page");
    return next;
  });
  const facets: ListFacet[] = [
    ...(["terms", "programs", "faculty"] as const).map((key) => {
      const label = key === "faculty" ? words.faculty : key === "terms" ? words.term : words.program;
      const filterKey = key === "terms" ? "term_id" : key === "programs" ? "program_id" : "faculty_id";
      const value = params.get(filterKey) ?? "";
      const options = [{ value: "", label: text.all(key) }, ...(query.data?.facets[key] ?? []).flatMap((v) =>
        v.value === null ? [] : [{ value: v.value, label: `${v.label} (${v.count})` }])];
      if (value && !options.some((o) => o.value === value)) options.push({ value, label: text.selected2(label.toLowerCase(), query.isPending ? " (reading)" : " is not in this read") });
      return { label, value, options, onChange: (next: string) => filter(filterKey, next),
        read: query.isPending ? { status: "loading" as const } : query.isError ? { status: "failed" as const, sentence: words.filterChoicesCouldNotBeRefreshed, retry: () => { void query.refetch(); } } : undefined };
    }),
    { label: words.source2, value: params.get("provenance") ?? "", options: [{ value: "", label: words.allSources },
      { value: "native", label: words.native }, { value: "imported", label: words.imported }], onChange: (v: string) => filter("provenance", v) },
    { label: words.continuation, value: continuation ?? "", options: [{ value: "", label: words.anyContinuation },
      { value: "true", label: words.continuationRecorded }, { value: "false", label: words.noContinuationRecorded }], onChange: (v: string) => filter("has_continuation", v) },
    ...(["roster", "credits", "outcomes", "attendance", "materials"] as const).map((key) => ({
      label: text.coverage(key[0].toUpperCase(), key.slice(1)), value: params.get(key) ?? "",
      options: [{ value: "", label: text.anyCoverage(key) }, { value: "present", label: text.present(key) },
        { value: "explicitly_omitted", label: text.explicitlyOmitted2(key) }, { value: "never_sourced", label: text.neverSourced(key) }],
      onChange: (v: string) => filter(key, v),
    })),
  ];
  const register = historyBox({ kind: "list", id: "history-sections", job: words.findAClosedSectionAndVisitItsHistoricalRecord, label: copy.sections,
    owner: { system: "imam" }, read: historyRead(query), fold: "never",
    detail: (v: HistoryPage) => text.historicalSectionsMatch(v.total),
    state: (v: HistoryPage) => ({ class: "quiet", word: v.items.length ? words.closedTermHistory : words.noHistoricalSectionsMatch }),
    rows: (v: HistoryPage) => v.items,
    row: { key: (r) => r.id, title: (r) => `${r.code} · ${r.title}`,
      facts: (r) => `${r.term_name} · ${r.instructors?.map((i) => i.display_name).join(", ") || r.faculty_label || "No instructor of record"} · ${r.student_count} ${r.student_count === 1 ? "student" : "students"}`,
      act: (r) => ({ verb: copy.readClassRecord, does: "visit", visit: `course:${r.id}/history` }) },

  });
  if (query.error instanceof ApiError && query.error.status === 403) return <PageRefusal id="course-history" journey={copy.history} refusal={{ reason: "forbidden", sentence: copy.forbidden, back: { label: copy.goBack, to: "/admin" } }} />;
  const primaryTerm = facets[0];
  const secondary = facets.slice(1);
  const activeCount = secondary.filter((facet) => facet.value !== "").length;
  const choice = (facet: ListFacet) => <label key={facet.label} className="grid gap-1 text-ui">{facet.label}
    <select value={facet.value} disabled={facet.read?.status === "loading"} onChange={(event) => facet.onChange(event.target.value)}>
      {facet.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
    </select>
    {facet.read?.status === "failed" && <span role="alert">{facet.read.sentence} <button type="button" onClick={facet.read.retry}>Try again</button></span>}
  </label>;
  return <div className="app-page history-register"><RegistrarLocalNavigation area="history" />
    <div className="history-find">
      <label className="grid gap-1 text-ui">{copy.searchClasses}<input aria-label={words.searchCourseHistory} placeholder={words.searchCourseHistory} value={params.get("q") ?? ""} onChange={(event) => filter("q", event.target.value)} /></label>
      {choice(primaryTerm)}
    </div>
    <details className="history-more-filters">
      <summary>{copy.moreFilters}{activeCount > 0 ? ` (${activeCount} active)` : ""}</summary>
      <div className="history-filter-grid">{secondary.map(choice)}</div>
    </details>
    <Bento id="course-history" journey={copy.history} boxes={[register]} defaultOpen={[register.id]} />
    {query.data && query.data.total > query.data.page_size && <nav aria-label={words.historyPages} className="flex gap-4 text-ui">
      <button type="button" disabled={filters.page <= 1} onClick={() => filter("page", String(filters.page! - 1))}>{words.previous}</button>
      <span>{words.page}{filters.page}</span>
      <button type="button" disabled={filters.page * 25 >= query.data.total} onClick={() => filter("page", String(filters.page! + 1))}>{words.next}</button>
    </nav>}
  </div>;
}
